import React, { useContext, useState, useEffect } from 'react';
import { Navbar, Container, Nav, NavDropdown, Button, Offcanvas, Modal, Spinner, Row, Col } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';
import { useTranslation } from 'react-i18next';
import { BsGlobe, BsBuilding } from 'react-icons/bs';
import { UtensilsCrossed, Link2 } from 'lucide-react';
import Jdenticon from 'react-jdenticon';
import AuthContext from '../context/AuthContext';
import usePermissions, { PERMISSIONS } from '../hooks/usePermissions';
import BusinessSwitcherModal from './BusinessSwitcherModal';
// ⚠ Temporal: selector de paleta. Ver src/theme/.
import ThemeSwitcher from './ThemeSwitcher';
import { getTables } from '../services/api';
import toast from 'react-hot-toast';

// ─── TableSelectorModal ───────────────────────────────────────────────────────

const TableSelectorModal = ({ show, onHide, businessSlug }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!show) return;
        setLoading(true);
        getTables()
            .then(res => setTables(res.data))
            .catch(() => setTables([]))
            .finally(() => setLoading(false));
    }, [show]);

    const tableUrl = (table) =>
        `${window.location.origin}/business/${businessSlug}?table_id=${table.table_id}&table=${encodeURIComponent(table.name)}`;

    const quickSaleUrl = () =>
        `${window.location.origin}/business/${businessSlug}`;

    const copyLink = (url, e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(url).then(() => {
            toast.success(t('tableSelector.linkCopied'));
        });
    };

    const handleSelect = (table) => {
        onHide();
        navigate(`/business/${businessSlug}?table_id=${table.table_id}&table=${encodeURIComponent(table.name)}&employee=true`);
    };

    const handleQuickSale = () => {
        onHide();
        navigate(`/business/${businessSlug}?employee=true`);
    };

    return (
        <Modal show={show} onHide={onHide} centered size="md">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fs-5 fw-bold">
                    <UtensilsCrossed size={18} className="me-2 text-primary" />
                    {t('tableSelector.title')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-2 pb-3 px-4">
                {loading ? (
                    <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
                ) : tables.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                        <UtensilsCrossed size={32} className="mb-2 opacity-25" />
                        <p className="mb-0 small">{t('tableSelector.noTables')}</p>
                    </div>
                ) : (
                    <Row xs={2} className="g-2 mb-3">
                        {tables.map(table => (
                            <Col key={table.table_id}>
                                <div
                                    role="button"
                                    onClick={() => handleSelect(table)}
                                    style={{
                                        border: '1.5px solid var(--ev-border-default)',
                                        borderRadius: 12,
                                        padding: '14px 12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        textAlign: 'center',
                                        position: 'relative',
                                        backgroundColor: 'var(--ev-bg-surface)',
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.borderColor = 'var(--ev-action-primary-bg)';
                                        e.currentTarget.style.backgroundColor = 'var(--ev-row-hover-bg)';
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.borderColor = 'var(--ev-border-default)';
                                        e.currentTarget.style.backgroundColor = 'var(--ev-bg-surface)';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <UtensilsCrossed size={22} className="mb-1 text-primary opacity-75" />
                                    <div className="fw-semibold small" style={{ lineHeight: 1.3 }}>{table.name}</div>
                                    {/* Botón copiar link — esquina superior derecha */}
                                    <button
                                        title={t('tableSelector.copyTableLink')}
                                        onClick={(e) => copyLink(tableUrl(table), e)}
                                        style={{
                                            position: 'absolute', top: 6, right: 6,
                                            background: 'var(--ev-bg-subtle)', border: 'none',
                                            borderRadius: 6, padding: '3px 5px',
                                            cursor: 'pointer', color: 'var(--ev-text-muted)', lineHeight: 1,
                                            transition: 'all 0.15s ease',
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.background = 'var(--ev-action-primary-bg)';
                                            e.currentTarget.style.color = 'var(--ev-action-primary-fg)';
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.background = 'var(--ev-bg-subtle)';
                                            e.currentTarget.style.color = 'var(--ev-text-muted)';
                                        }}
                                    >
                                        <Link2 size={13} />
                                    </button>
                                </div>
                            </Col>
                        ))}
                    </Row>
                )}

                {/* Venta Rápida — separador inferior */}
                <div className="border-top pt-3 d-flex align-items-center gap-2">
                    <Button
                        variant="outline-secondary"
                        className="flex-grow-1"
                        style={{ borderRadius: 10 }}
                        onClick={handleQuickSale}
                    >
                        {t('tableSelector.quickSale')}
                    </Button>
                    <button
                        title={t('tableSelector.copyQuickSaleLink')}
                        onClick={(e) => copyLink(quickSaleUrl(), e)}
                        style={{
                            background: 'var(--ev-bg-subtle)', border: 'none',
                            borderRadius: 8, padding: '7px 10px',
                            cursor: 'pointer', color: 'var(--ev-text-muted)', lineHeight: 1,
                            transition: 'all 0.15s ease',
                            flexShrink: 0,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--ev-action-primary-bg)'; e.currentTarget.style.color = 'var(--ev-action-primary-fg)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'var(--ev-bg-subtle)'; e.currentTarget.style.color = 'var(--ev-text-muted)'; }}
                    >
                        <Link2 size={15} />
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

const AppNavbar = () => {
    const { user, business, logoutUser } = useContext(AuthContext);
    const { hasPermission } = usePermissions(user);
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const [showBusinessModal, setShowBusinessModal] = useState(false);
    const [showTableSelector, setShowTableSelector] = useState(false);
    const [expanded, setExpanded] = useState(false);
    // Dropdown abierto en la barra principal: 'operations' | 'finance' | 'catalog' | null
    const [openMenu, setOpenMenu] = useState(null);
    const menuTimerRef = React.useRef(null);

    // Rutas que "cuelgan" de cada grupo — para marcar activo el toggle padre
    const OPERATIONS_ROUTES = ['/production', '/quotes', '/payment-notifications'];
    const FINANCE_ROUTES = ['/expenses', '/incomes', '/settlements'];
    const CATALOG_ROUTES = ['/products', '/clients', '/suppliers'];
    const isOperationsActive = OPERATIONS_ROUTES.includes(location.pathname);
    const isFinanceActive = FINANCE_ROUTES.includes(location.pathname);
    const isCatalogActive = CATALOG_ROUTES.includes(location.pathname);

    // Detectar si estamos en rutas de configuración (settings)
    const isInSettings = location.pathname.startsWith('/settings');

    // Función helper para determinar si un enlace del navbar principal debe estar activo
    // Retorna false si estamos en /settings/*, para que ningún link del navbar principal esté marcado
    const isNavLinkActive = (path) => {
        if (isInSettings) return false;
        return location.pathname === path;
    };

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
        setExpanded(false); // Cerrar menú al cambiar idioma
    };

    const handleNavClick = () => {
        setExpanded(false); // Cerrar menú (offcanvas) al hacer clic en un enlace
        setOpenMenu(null);  // Cerrar cualquier dropdown al navegar
    };

    // Hover-intent compartido por los tres dropdowns de la barra (Operación / Finanzas / Catálogo).
    // En escritorio abren al pasar el mouse y cierran con 300ms de gracia; en táctil, con click (onToggle).
    const openMenuNow = (id) => {
        if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
        setOpenMenu(id);
    };

    const closeMenuDelayed = () => {
        menuTimerRef.current = setTimeout(() => setOpenMenu(null), 300);
    };

    const toggleMenu = (id, isOpen) => {
        if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
        setOpenMenu(isOpen ? id : null);
    };

    // Evitar fugas: limpiar el timer pendiente al desmontar
    useEffect(() => () => {
        if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
    }, []);

    const handleBusinessModalOpen = () => {
        setShowBusinessModal(true);
        setExpanded(false); // Cerrar menú al abrir el modal
    };

    // ── Visibilidad de cada grupo de la barra ────────────────────────────────
    // Un dropdown solo se renderiza si al menos uno de sus hijos es visible
    // (permiso del usuario + feature del plan). Si queda vacío, no aparece.
    const canTakeOrder = !!(business && hasPermission(PERMISSIONS.CREATE_SALES) && business.slug);
    // Tablero de Producción (tarea 031): es una vista de ventas, sin feature de plan.
    const canViewProductionBoard = !!(business && hasPermission(PERMISSIONS.VIEW_SALES));
    const canViewQuotes = !!(business && hasPermission(PERMISSIONS.VIEW_QUOTES));
    const canViewPaymentNotifications = !!business?.plan_features?.payment_notifications;
    const showOperationsMenu = canTakeOrder || canViewProductionBoard || canViewQuotes || canViewPaymentNotifications;

    const canViewExpenses = !!(business && hasPermission(PERMISSIONS.VIEW_EXPENSES));
    const canViewIncomes = !!(business && hasPermission(PERMISSIONS.VIEW_INCOMES) && business?.plan_features?.incomes);
    const canViewSettlements = !!(canViewExpenses && business?.plan_features?.supplier_settlements);
    const showFinanceMenu = canViewExpenses || canViewIncomes || canViewSettlements;

    const canViewProducts = !!(business && hasPermission(PERMISSIONS.VIEW_PRODUCTS));
    const canViewClients = !!(business && hasPermission(PERMISSIONS.VIEW_CLIENTS));
    const canViewSuppliers = !!(business && hasPermission(PERMISSIONS.VIEW_SUPPLIERS));
    const showCatalogMenu = canViewProducts || canViewClients || canViewSuppliers;

    return (
        <>
            <Navbar
                bg="light"
                variant="light"
                expand="lg"
                className="shadow-sm"
                expanded={expanded}
                onToggle={setExpanded}
            >
                <Container>
                    <Navbar.Brand as={Link} to="/" className="d-flex align-items-center">
                        <img
                            src="/logo_evazard_transparente.svg"
                            alt="Evazard Logo"
                            height="40"
                            className="me-2"
                        />
                        <span className="fw-bold text-primary">Evazard</span>
                    </Navbar.Brand>
                    <Navbar.Toggle aria-controls="offcanvasNavbar-expand-lg" />
                    <Navbar.Offcanvas
                        id="offcanvasNavbar-expand-lg"
                        aria-labelledby="offcanvasNavbarLabel-expand-lg"
                        placement="end"
                        restoreFocus={false}
                        className="custom-offcanvas-width"
                    >
                        <Offcanvas.Header closeButton>
                            <Offcanvas.Title id="offcanvasNavbarLabel-expand-lg">
                                <img
                                    src="/logo_evazard_transparente.svg"
                                    alt="Evazard Logo"
                                    height="30"
                                    className="me-2"
                                />
                                Evazard
                            </Offcanvas.Title>
                        </Offcanvas.Header>
                        <Offcanvas.Body className="d-flex flex-column flex-lg-row p-0">
                            {/* 1. User & Business Section (Mobile: Top, Desktop: Rightmost) */}
                            <Nav className="d-flex flex-row justify-content-end align-items-center gap-3 pe-3 mb-3 mb-lg-0 order-1 order-lg-3">
                                {/* Business Switcher - available for all authenticated users */}
                                {user && business && (
                                    <NavDropdown
                                        title={business?.name || 'Negocio'}
                                        id="business-nav-dropdown"
                                        align="end"
                                    >
                                        <NavDropdown.Item onClick={() => { setShowBusinessModal(true); handleNavClick(); }}>
                                            {t('navigation.switch_business')}
                                        </NavDropdown.Item>
                                        {hasPermission(PERMISSIONS.MANAGE_USERS) && (
                                            <>
                                                <NavDropdown.Divider />
                                                <LinkContainer to="/settings/users" onClick={handleNavClick}>
                                                    <NavDropdown.Item>{t('navigation.collaborators')}</NavDropdown.Item>
                                                </LinkContainer>
                                            </>
                                        )}
                                    </NavDropdown>
                                )}

                                {/* User Dropdown */}
                                <NavDropdown
                                    title={
                                        business?.logo_url ? (
                                            <img
                                                src={business.logo_url}
                                                alt={business.name}
                                                width="32"
                                                height="32"
                                                className="rounded"
                                                style={{ objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <Jdenticon value={user?.email} size="24" bgColor="#FF69B4" />
                                        )
                                    }
                                    id="basic-nav-dropdown"
                                    align="end"
                                >
                                    <NavDropdown.ItemText className="text-muted small">
                                        {user?.email}
                                    </NavDropdown.ItemText>
                                    <NavDropdown.Divider />
                                    {user?.role === 'superadmin' && (
                                        <>
                                            <LinkContainer to="/admin/dashboard" onClick={handleNavClick}>
                                                <NavDropdown.Item>Panel de Administración</NavDropdown.Item>
                                            </LinkContainer>
                                            <NavDropdown.Divider />
                                        </>
                                    )}
                                    <LinkContainer to="/settings" onClick={handleNavClick}>
                                        <NavDropdown.Item>{t('navigation.settings')}</NavDropdown.Item>
                                    </LinkContainer>
                                    <NavDropdown.Divider />
                                    <NavDropdown.Item onClick={() => { logoutUser(); handleNavClick(); }}>
                                        {t('navigation.logout')}
                                    </NavDropdown.Item>
                                </NavDropdown>
                            </Nav>

                            {/* 2. Main Navigation Links (Mobile: Middle, Desktop: Leftmost/Main) */}
                            <Nav className="align-items-end align-items-lg-center pe-3 me-lg-auto order-2 order-lg-1 mobile-nav-container">
                                {/* ── Planos (uso diario): Ventas · Estadísticas · Caja ── */}
                                {business && hasPermission(PERMISSIONS.VIEW_SALES) && (
                                    <Nav.Link
                                        as={Link}
                                        to="/sales"
                                        onClick={handleNavClick}
                                        active={isNavLinkActive('/sales')}
                                    >
                                        {t('navigation.sales')}
                                    </Nav.Link>
                                )}
                                {business && hasPermission(PERMISSIONS.VIEW_DASHBOARD) && (
                                    <Nav.Link
                                        as={Link}
                                        to="/dashboard"
                                        onClick={handleNavClick}
                                        active={isNavLinkActive('/dashboard')}
                                    >
                                        {t('navigation.dashboard')}
                                    </Nav.Link>
                                )}
                                {business && hasPermission(PERMISSIONS.VIEW_CASH_REGISTER) && (
                                    <Nav.Link
                                        as={Link}
                                        to="/cash-register"
                                        onClick={handleNavClick}
                                        active={isNavLinkActive('/cash-register')}
                                    >
                                        {t('navigation.cashRegister')}
                                    </Nav.Link>
                                )}

                                {/* ── Operación ▾ : Tomar Pedido · Producción · Cotizaciones · Notificaciones de Pago ── */}
                                {showOperationsMenu && (
                                    <NavDropdown
                                        title={t('navigation.groupOperations')}
                                        id="operations-nav-dropdown"
                                        data-testid="nav-group-operations"
                                        show={openMenu === 'operations'}
                                        onMouseEnter={() => openMenuNow('operations')}
                                        onMouseLeave={closeMenuDelayed}
                                        onToggle={(isOpen) => toggleMenu('operations', isOpen)}
                                        align="end"
                                        className={!isInSettings && isOperationsActive ? 'active' : ''}
                                    >
                                        {canTakeOrder && (
                                            <NavDropdown.Item
                                                onClick={() => { handleNavClick(); setShowTableSelector(true); }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <UtensilsCrossed size={14} className="me-1" />
                                                {t('navigation.takeOrder')}
                                            </NavDropdown.Item>
                                        )}
                                        {canViewProductionBoard && (
                                            <NavDropdown.Item
                                                as={Link}
                                                to="/production"
                                                onClick={handleNavClick}
                                                active={isNavLinkActive('/production')}
                                            >
                                                {t('navigation.productionBoard')}
                                            </NavDropdown.Item>
                                        )}
                                        {canViewQuotes && (
                                            <NavDropdown.Item
                                                as={Link}
                                                to="/quotes"
                                                onClick={handleNavClick}
                                                active={isNavLinkActive('/quotes')}
                                            >
                                                {t('navigation.quotes')}
                                            </NavDropdown.Item>
                                        )}
                                        {canViewPaymentNotifications && (
                                            <NavDropdown.Item
                                                as={Link}
                                                to="/payment-notifications"
                                                onClick={handleNavClick}
                                                active={isNavLinkActive('/payment-notifications')}
                                            >
                                                {t('navigation.payment_notifications')}
                                            </NavDropdown.Item>
                                        )}
                                    </NavDropdown>
                                )}

                                {/* ── Finanzas ▾ : Gastos · Otros ingresos · Liquidaciones ── */}
                                {showFinanceMenu && (
                                    <NavDropdown
                                        title={t('navigation.groupFinance')}
                                        id="finance-nav-dropdown"
                                        data-testid="nav-group-finance"
                                        show={openMenu === 'finance'}
                                        onMouseEnter={() => openMenuNow('finance')}
                                        onMouseLeave={closeMenuDelayed}
                                        onToggle={(isOpen) => toggleMenu('finance', isOpen)}
                                        align="end"
                                        className={!isInSettings && isFinanceActive ? 'active' : ''}
                                    >
                                        {canViewExpenses && (
                                            <NavDropdown.Item
                                                as={Link}
                                                to="/expenses"
                                                onClick={handleNavClick}
                                                active={isNavLinkActive('/expenses')}
                                            >
                                                {t('navigation.expenses')}
                                            </NavDropdown.Item>
                                        )}
                                        {canViewIncomes && (
                                            <NavDropdown.Item
                                                as={Link}
                                                to="/incomes"
                                                onClick={handleNavClick}
                                                active={isNavLinkActive('/incomes')}
                                            >
                                                {t('navigation.incomes')}
                                            </NavDropdown.Item>
                                        )}
                                        {canViewSettlements && (
                                            <NavDropdown.Item
                                                as={Link}
                                                to="/settlements"
                                                onClick={handleNavClick}
                                                active={isNavLinkActive('/settlements')}
                                            >
                                                {t('navigation.settlements')}
                                            </NavDropdown.Item>
                                        )}
                                    </NavDropdown>
                                )}

                                {/* ── Catálogo ▾ : Productos · Clientes · Proveedores ── */}
                                {showCatalogMenu && (
                                    <NavDropdown
                                        title={t('navigation.groupCatalog')}
                                        id="catalog-nav-dropdown"
                                        data-testid="nav-group-catalog"
                                        show={openMenu === 'catalog'}
                                        onMouseEnter={() => openMenuNow('catalog')}
                                        onMouseLeave={closeMenuDelayed}
                                        onToggle={(isOpen) => toggleMenu('catalog', isOpen)}
                                        align="end"
                                        className={!isInSettings && isCatalogActive ? 'active' : ''}
                                    >
                                        {canViewProducts && (
                                            <NavDropdown.Item
                                                as={Link}
                                                to="/products"
                                                onClick={handleNavClick}
                                                active={isNavLinkActive('/products')}
                                            >
                                                {t('navigation.products')}
                                            </NavDropdown.Item>
                                        )}
                                        {canViewClients && (
                                            <NavDropdown.Item
                                                as={Link}
                                                to="/clients"
                                                onClick={handleNavClick}
                                                active={isNavLinkActive('/clients')}
                                            >
                                                {t('navigation.clients')}
                                            </NavDropdown.Item>
                                        )}
                                        {canViewSuppliers && (
                                            <NavDropdown.Item
                                                as={Link}
                                                to="/suppliers"
                                                onClick={handleNavClick}
                                                active={isNavLinkActive('/suppliers')}
                                            >
                                                {t('navigation.suppliers')}
                                            </NavDropdown.Item>
                                        )}
                                    </NavDropdown>
                                )}
                            </Nav>

                            {/* 3. Language Selector (Mobile: Bottom/Last, Desktop: Middle/Right) */}
                            <Nav className="align-items-end align-items-lg-center pe-3 order-3 order-lg-2">
                                {/* ⚠ Temporal: selector de paleta mientras se elige cuál se queda.
                                    Va aquí, al lado del idioma, para poder comparar sin salir de
                                    la pantalla que se está mirando. Ver src/theme/. */}
                                <ThemeSwitcher />
                                {/* Language Selector */}
                                <NavDropdown
                                    title={
                                        <>
                                            <BsGlobe className="me-2" />
                                            {i18n.language.split('-')[0].toUpperCase()}
                                        </>
                                    }
                                    id="language-nav-dropdown"
                                    align="end"
                                >
                                    <NavDropdown.Item onClick={() => changeLanguage('en')}>English</NavDropdown.Item>
                                    <NavDropdown.Item onClick={() => changeLanguage('es')}>Español</NavDropdown.Item>
                                </NavDropdown>
                            </Nav>
                        </Offcanvas.Body>
                    </Navbar.Offcanvas>
                </Container>
            </Navbar>

            <BusinessSwitcherModal
                show={showBusinessModal}
                onHide={() => setShowBusinessModal(false)}
            />

            <TableSelectorModal
                show={showTableSelector}
                onHide={() => setShowTableSelector(false)}
                businessSlug={business?.slug}
            />
        </>
    );
};

export default AppNavbar;
